import { useConvex } from "convex/react";
import { Calendar, ExternalLink, Globe, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { api } from "../../../convex/_generated/api";

interface PublishedTour {
  id: string;
  project_id: string;
  bucket_name?: string;
  folder_name?: string;
  tour_url?: string;
  bucket_url?: string;
  published_at?: string;
  created_at?: string;
  project_name?: string;
}

export const PublishedToursList = () => {
  const convex = useConvex();
  const { user } = useAuth();
  const [publishedTours, setPublishedTours] = useState<PublishedTour[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadPublishedTours = useCallback(async () => {
    try {
      setLoading(true);

      const tours = await convex.query(api.publishedTours.listMine, {});
      setPublishedTours((tours || []) as PublishedTour[]);
    } catch (error) {
      console.error("Error loading published tours:", error);
      toast({
        variant: "destructive",
        title: "Load failed",
        description: "Failed to load published tours.",
      });
    } finally {
      setLoading(false);
    }
  }, [convex, toast]);

  useEffect(() => {
    if (!user?.id) {
      setPublishedTours([]);
      setLoading(false);
      return;
    }

    void loadPublishedTours();
  }, [user?.id, loadPublishedTours]);

  const deletePublishedTour = async (tourId: string) => {
    try {
      await convex.mutation(api.publishedTours.remove, { id: tourId });

      setPublishedTours((prev) => prev.filter((tour) => tour.id !== tourId));

      toast({
        title: "Tour deleted",
        description: "Published tour has been removed.",
      });
    } catch (error) {
      console.error("Error deleting published tour:", error);
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: "Failed to delete the published tour.",
      });
    }
  };

  const formatDate = (dateString?: string) => {
    const source = dateString || new Date().toISOString();
    return new Date(source).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-400">Loading published tours...</div>
      </div>
    );
  }

  if (publishedTours.length === 0) {
    return (
      <div className="text-center p-8">
        <Globe size={48} className="mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-300 mb-2">No Published Tours</h3>
        <p className="text-gray-400 text-sm">
          You haven't published any tours yet. Use the Export dialog to publish your first tour.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-white mb-4">Published Tours</h3>

      {publishedTours.map((tour) => (
        <Card key={tour.id} className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-base">
                {tour.project_name || "Unknown Project"}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    tour.tour_url && window.open(tour.tour_url, "_blank", "noopener,noreferrer")
                  }
                  className="border-green-600 text-green-400 hover:bg-green-600 hover:text-white"
                  disabled={!tour.tour_url}
                >
                  <ExternalLink size={14} className="mr-1" />
                  Open Tour
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => deletePublishedTour(tour.id)}
                  className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-0">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-gray-300">
                <Calendar size={14} />
                <span>Published: {formatDate(tour.published_at || tour.created_at)}</span>
              </div>

              <div className="flex items-center gap-2 text-gray-400">
                <Globe size={14} />
                <span>Bucket: {tour.bucket_name || "N/A"}</span>
              </div>

              <div className="text-gray-400">
                <span>Folder: {tour.folder_name || "N/A"}</span>
              </div>

              {tour.tour_url && (
                <div className="pt-2">
                  <input
                    type="text"
                    value={tour.tour_url}
                    readOnly
                    className="w-full bg-gray-700 border border-gray-600 text-gray-300 px-3 py-2 rounded text-xs"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
